/**
 * Common components and utilities for Meeting Debrief Views
 */

import React, { useMemo } from 'react';
import { Text, Flex, Icon, Avatar } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { useUsers } from 'App/components/hooks/appHooks';
import type { Topic, Person } from 'App/resources/Model/MeetingDebrief';
import { ColorsEnum } from '@upsales/components/Utils/colors';
import { SizesLarge } from '@upsales/components/Utils/Sizes';

// ============================================================================
// Helper Functions
// ============================================================================

export const parseDebrief = <T,>(data: unknown): T | null => {
	if (!data) {
		return null;
	}

	if (typeof data === 'object' && data !== null) {
		return data as T;
	}

	if (typeof data === 'string') {
		try {
			return JSON.parse(data) as T;
		} catch (error) {
			console.error('Failed to parse debrief JSON:', error);
			return null;
		}
	}

	return null;
};

export const formatStatus = (status: string): string => {
	return status.replace(/_/g, ' ').replace(/-/g, ' ');
};

// ============================================================================
// Summary Card Component
// ============================================================================

export interface SummaryCardProps {
	summary: string;
	children?: React.ReactNode;
	classes: BemClass;
	t: (text: string) => string;
	hideTitle?: boolean;
}

export function SummaryCard({ summary, children, classes, t, hideTitle }: SummaryCardProps) {
	return (
		<div className={classes.elem('summaryCard').b()}>
			{!hideTitle ? (
				<Flex justifyContent="space-between" alignItems="flex-start" spacing="mb3">
					<Flex alignItems="center" gap="u1" spacing="p1" className={classes.elem('summaryBadge').b()}>
						<Icon name="stars" color="beta-600" />
						<Text size="sm" color="beta-600">
							{t('meetingDebrief.summary')}
						</Text>
					</Flex>
					<Flex
						alignItems="baseline"
						gap="u2"
						className={classes.elem('summaryLink').b()}
						onClick={(e: React.MouseEvent) => {
							e.preventDefault();
							window.open(
								'https://support.upsales.com/hc/en-us/articles/41542035750161-How-Upsales-Assesses-Your-Deals',
								'_blank',
								'noopener,noreferrer'
							);
						}}
					>
						<Icon name="question-circle" color="info-500" />
						<Text size="sm" color="info-500">
							{t('meetingDebrief.learnAboutIntelligence')}
						</Text>
					</Flex>
				</Flex>
			) : null}
			<Text size="md" color="grey-13" className={classes.elem('summaryText').b()}>
				{summary}
			</Text>
			{children}
		</div>
	);
}

// ============================================================================
// User Avatar Component
// ============================================================================

interface UserAvatarProps {
	owner: string;
	size?: 'sm' | 'md' | 'lg' | 'xl' | number;
}

export function UserAvatar({ owner, size = 'sm' }: UserAvatarProps) {
	const users = useUsers('active', false, 'all', true);

	const nameWithoutCompany = owner.replace(/\s*\([^)]*\)\s*$/, '').trim();

	const matchedUser = useMemo(() => {
		return users.find((user: { name?: string; firstName?: string; lastName?: string }) => {
			const userName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
			return userName === nameWithoutCompany || userName.toLowerCase() === nameWithoutCompany.toLowerCase();
		});
	}, [users, nameWithoutCompany]);

	if (!matchedUser) {
		return <Icon name="user" color="neutral-600" />;
	}

	return <Avatar size={size} initials={nameWithoutCompany} email={(matchedUser as { email?: string }).email} />;
}

// ============================================================================
// Bullet Point Component
// ============================================================================

interface BulletPointProps {
	children: React.ReactNode;
	color?: keyof typeof ColorsEnum;
	size?: SizesLarge;
	className?: string;
}

export function BulletPoint({ color = 'black', size = 'md', children, className }: BulletPointProps) {
	return (
		<Flex gap="10" alignItems="flex-start" spacing="pl2" className={className}>
			<Text color={color} size={size}>
				{'• '}
				{children}
			</Text>
		</Flex>
	);
}

// ============================================================================
// Status Badge Component
// ============================================================================

export type StatusBadgeVariant = 'solid' | 'health' | 'severity' | 'engagement';

export interface StatusBadgeProps {
	variant: StatusBadgeVariant;
	value: string | boolean;
	t: (text: string) => string;
}

export function StatusBadge({ variant, value, t }: StatusBadgeProps) {
	const classes = new BemClass('MeetingDebriefView');

	const normalizedValue = typeof value === 'boolean' ? String(value) : value.toLowerCase().replace('-', '_');

	let displayText: string = '';
	let displayIcon: React.ReactNode = null;
	let modifierClass = '';

	if (variant === 'solid') {
		displayText = value ? t('meetingDebrief.solid') : t('meetingDebrief.weak');
		displayIcon = value ? <Icon name="check" color="success-600" /> : <Icon name="ban" color="danger-600" />;
		modifierClass = value ? 'solid' : 'weak';
	} else if (variant === 'engagement') {
		const isEngaged = normalizedValue === 'engaged' || normalizedValue === 'true';
		displayText = isEngaged ? t('meetingDebrief.engaged') : t('meetingDebrief.notEngaged');
		modifierClass = isEngaged ? 'engaged' : 'notEngaged';
	} else if (variant === 'health') {
		displayText = String(value).replace('-', ' ').replace('_', ' ');
		displayText = displayText.charAt(0).toUpperCase() + displayText.slice(1);
		modifierClass = normalizedValue;
	} else {
		displayText = String(value).replace('-', ' ').replace('_', ' ');
		displayText = displayText.charAt(0).toUpperCase() + displayText.slice(1);
		modifierClass = normalizedValue;
	}

	return (
		<span className={classes.elem('statusBadge').mod(modifierClass).b()}>
			<Text size="xs">
				{displayIcon}
				{displayIcon && displayText ? ' ' : ''}
				{displayText}
			</Text>
		</span>
	);
}

// ============================================================================
// List Item Components
// ============================================================================

export interface ListItemData {
	name: string;
	secondaryText?: string;
	badge?: { value: boolean | string };
	deadline?: string | null;
	description?: string;
}

interface ListItemListProps {
	items: ListItemData[];
	classes: BemClass;
	t?: (text: string) => string;
}

export function ListItemList({ items, classes, t }: ListItemListProps) {
	return (
		<>
			{items.map((item, idx) => {
				const isLast = idx === items.length - 1;

				return (
					<div
						key={`item-${idx}`}
						className={classes
							.elem('actionItem')
							.mod(isLast ? 'last' : '')
							.b()}
					>
						<Flex gap="u2" alignItems="center" spacing={isLast ? undefined : 'mb1'}>
							<UserAvatar owner={item.name} size="sm" />
							<Flex direction="column" gap="u1" className={classes.elem('listItemContent').b()}>
								<Flex gap="u1" alignItems="center">
									<Text size="sm" bold color="black">
										{item.name}
									</Text>
									{item.secondaryText && (
										<>
											<Text size="sm" color="grey-11">
												{'• '}
												{item.secondaryText}
												{' •'}
											</Text>
										</>
									)}
									{item.badge && t && (
										<StatusBadge variant="engagement" value={item.badge.value} t={t} />
									)}
									{item.deadline && (
										<Text size="sm" color="grey-11">
											{typeof item.deadline === 'string'
												? new Date(item.deadline).toLocaleDateString('en-GB', {
														day: 'numeric',
														month: 'short',
														year: 'numeric'
												  })
												: item.deadline}
										</Text>
									)}
								</Flex>
								{item.description && (
									<Text size="sm" color="grey-11">
										{item.description}
									</Text>
								)}
							</Flex>
						</Flex>
					</div>
				);
			})}
		</>
	);
}

// ============================================================================
// Action Item List Component
// ============================================================================

interface ActionItemListProps {
	actions: Array<{ task: string; owner: string; deadline: string | null }>;
	classes: BemClass;
}

export function ActionItemList({ actions, classes }: ActionItemListProps) {
	const items: ListItemData[] = actions.map(action => {
		const ownerName = action.owner.replace(/\s*\([^)]*\)\s*$/, '').trim();
		const companyMatch = action.owner.match(/\(([^)]+)\)/);
		const companyName = companyMatch ? companyMatch[1] : null;

		return {
			name: ownerName,
			secondaryText: companyName || undefined,
			deadline: action.deadline || undefined,
			description: action.task
		};
	});

	return <ListItemList items={items} classes={classes} />;
}

// ============================================================================
// Person Item List Component
// ============================================================================

interface PersonItemListProps {
	people: Person[];
	classes: BemClass;
	t: (text: string) => string;
}

export function PersonItemList({ people, classes, t }: PersonItemListProps) {
	const items: ListItemData[] = people.map(person => {
		const isEngaged =
			person.stance?.toLowerCase().includes('engagerad') || person.stance?.toLowerCase().includes('engaged');

		return {
			name: person.name,
			secondaryText: person.role || undefined,
			badge: {
				value: isEngaged
			},
			description: person.note || undefined
		};
	});

	return <ListItemList items={items} classes={classes} t={t} />;
}

// ============================================================================
// Discussed Section Component
// ============================================================================

interface DiscussedSectionProps {
	topics: Topic[];
	classes: BemClass;
}

export function DiscussedSection({ topics, classes }: DiscussedSectionProps) {
	return (
		<>
			{topics.map((topic: Topic, idx: number) => (
				<Flex
					key={`topic-${idx}`}
					direction="column"
					gap="u2"
					spacing={idx < topics.length - 1 ? 'mb4' : undefined}
				>
					<Text bold color="black">
						{topic.topic}
					</Text>
					<Flex direction="column" gap="u1">
						{topic.points.map((point: string, pIdx: number) => (
							<BulletPoint
								color="neutral-600"
								size="sm"
								key={`point-${idx}-${pIdx}`}
								className={classes.elem('discussedBullet').b()}
							>
								{point}
							</BulletPoint>
						))}
					</Flex>
				</Flex>
			))}
		</>
	);
}
